import React, { useState } from 'react';
import { Plus, X } from 'lucide-react';

const IssueTypeSelector = ({
  selectedIssues = [],
  customIssues = [],
  onSelectionChange,
  className = ''
}) => {
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customIssueText, setCustomIssueText] = useState('');

  const predefinedIssues = [
    { id: 'water_quality', label: 'Water Quality', color: 'bg-blue-100 text-blue-800 border-blue-200' },
    { id: 'flood_erosion', label: 'Flood/Erosion', color: 'bg-red-100 text-red-800 border-red-200' },
    { id: 'vegetation', label: 'Vegetation', color: 'bg-green-100 text-green-800 border-green-200' },
    { id: 'litter_pollution', label: 'Litter/Pollution', color: 'bg-orange-100 text-orange-800 border-orange-200' },
    { id: 'infrastructure', label: 'Infrastructure', color: 'bg-purple-100 text-purple-800 border-purple-200' },
    { id: 'wildlife', label: 'Wildlife', color: 'bg-teal-100 text-teal-800 border-teal-200' },
    { id: 'community_activity', label: 'Community Activity', color: 'bg-indigo-100 text-indigo-800 border-indigo-200' },
    { id: 'equipment_sensor', label: 'Equipment/Sensor', color: 'bg-cyan-100 text-cyan-800 border-cyan-200' }
  ];

  const handleIssueToggle = (issueId) => {
    const newSelectedIssues = selectedIssues.includes(issueId)
      ? selectedIssues.filter(id => id !== issueId)
      : [...selectedIssues, issueId];

    onSelectionChange({
      selectedIssues: newSelectedIssues,
      customIssues
    });
  };

  const handleCustomIssueAdd = () => {
    const trimmedText = customIssueText.trim();
    if (!trimmedText || customIssues.includes(trimmedText)) {
      return;
    }

    const newCustomIssues = [...customIssues, trimmedText];
    setCustomIssueText('');
    setShowCustomInput(false);

    onSelectionChange({
      selectedIssues,
      customIssues: newCustomIssues
    });
  };

  const handleCustomIssueRemove = (issueToRemove) => {
    const newCustomIssues = customIssues.filter(issue => issue !== issueToRemove);
    onSelectionChange({
      selectedIssues,
      customIssues: newCustomIssues
    });
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCustomIssueAdd();
    } else if (e.key === 'Escape') {
      setShowCustomInput(false);
      setCustomIssueText('');
    }
  };

  return (
    <div className={className}>
      <label className="block text-sm font-medium text-gray-700 mb-3">
        Issue Types
        <span className="text-gray-500 ml-1">(Select all that apply)</span>
      </label>

      {/* Predefined Issue Types */}
      <div className="space-y-3 mb-4">
        <div className="flex flex-wrap gap-2">
          {predefinedIssues.map((issue) => {
            const isSelected = selectedIssues.includes(issue.id);
            return (
              <button
                key={issue.id}
                type="button"
                onClick={() => handleIssueToggle(issue.id)}
                className={`
                  px-3 py-2 rounded-full text-sm font-medium transition-all duration-200 border
                  ${isSelected
                    ? `${issue.color} ring-2 ring-offset-1 ring-gray-300`
                    : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                  }
                `}
              >
                {issue.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Custom Issue Types */}
      {customIssues.length > 0 && (
        <div className="mb-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Custom Issues:</h4>
          <div className="flex flex-wrap gap-2">
            {customIssues.map((customIssue, index) => (
              <div
                key={index}
                className="inline-flex items-center bg-green-100 text-green-800 px-3 py-2 rounded-full text-sm font-medium border border-green-200"
              >
                <span>{customIssue}</span>
                <button
                  type="button"
                  onClick={() => handleCustomIssueRemove(customIssue)}
                  className="ml-2 text-green-600 hover:text-green-800 transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add Custom Issue */}
      <div className="space-y-2">
        {!showCustomInput ? (
          <button
            type="button"
            onClick={() => setShowCustomInput(true)}
            className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Custom Issue Type
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={customIssueText}
              onChange={(e) => setCustomIssueText(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder="Enter custom issue type..."
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
              autoFocus
            />
            <button
              type="button"
              onClick={handleCustomIssueAdd}
              disabled={!customIssueText.trim()}
              className="px-4 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Add
            </button>
            <button
              type="button"
              onClick={() => {
                setShowCustomInput(false);
                setCustomIssueText('');
              }}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-400 transition-colors"
            >
              Cancel
            </button>
          </div>
        )}
      </div>

      {/* Guidelines */}
      <div className="mt-4 text-xs text-gray-500 bg-gray-50 p-3 rounded-lg">
        <p className="font-medium mb-1">Guidelines:</p>
        <ul className="space-y-1">
          <li>• Select all issue types that apply to your report</li>
          <li>• You can add custom issue types if needed</li>
          <li>• Use descriptive custom names for specific issues</li>
        </ul>
      </div>
    </div>
  );
};

export default IssueTypeSelector;